import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext as NavigationContext, useNavigate } from "react-router-dom";

interface NavigationGuard {
  showConfirmation: boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
}

interface HistoryNavigator {
  push: (to: unknown, state?: unknown, opts?: unknown) => void;
  replace: (to: unknown, state?: unknown, opts?: unknown) => void;
}

const HISTORY_TRAVERSAL_GIVE_UP_MS = 300;
const LATE_POPSTATE_IGNORE_MS = 2000;

/**
 * Prevents navigation away from the page when a recording is active.
 * BrowserRouter's history has push and replace, not block(), so programmatic
 * navigate() is guarded by wrapping those methods. Link clicks and raw anchors
 * are caught separately. Browser close and refresh use beforeunload.
 * Returns state to drive a confirmation modal in the UI.
 */
export function useNavigationGuard(shouldBlock: boolean): NavigationGuard {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const allowNextNavigationRef = useRef(false);
  const shouldBlockRef = useRef(shouldBlock);
  const leavingRef = useRef(false);
  const inFlightTraversalRef = useRef(false);
  const traversalIdRef = useRef(0);
  const recordHrefRef = useRef(window.location.href);
  shouldBlockRef.current = shouldBlock;
  const navigate = useNavigate();
  const { navigator } = useContext(NavigationContext);
  const historyNavigator = navigator as HistoryNavigator;

  const requestConfirmation = useCallback((nextStep: () => void) => {
    pendingNavigationRef.current = nextStep;
    setShowConfirmation(true);
  }, []);

  useEffect(() => {
    if (!shouldBlock) {
      return undefined;
    }
    if (
      typeof historyNavigator.push !== "function" ||
      typeof historyNavigator.replace !== "function"
    ) {
      return undefined;
    }

    const originalPush = historyNavigator.push.bind(historyNavigator);
    const originalReplace = historyNavigator.replace.bind(historyNavigator);

    const guard =
      (original: HistoryNavigator["push"]): HistoryNavigator["push"] =>
      (to, state, opts) => {
        if (allowNextNavigationRef.current || !shouldBlockRef.current) {
          original(to, state, opts);
          return;
        }

        requestConfirmation(() => {
          allowNextNavigationRef.current = true;
          original(to, state, opts);
          setTimeout(() => {
            allowNextNavigationRef.current = false;
          }, 0);
        });
      };

    historyNavigator.push = guard(originalPush);
    historyNavigator.replace = guard(originalReplace);

    return () => {
      historyNavigator.push = originalPush;
      historyNavigator.replace = originalReplace;
    };
  }, [historyNavigator, requestConfirmation, shouldBlock]);

  useEffect(() => {
    if (!shouldBlock) return;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldBlock]);

  // Fallback for router links and sidebar clicks when block() is unavailable or inconsistent.
  useEffect(() => {
    if (!shouldBlock) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (allowNextNavigationRef.current) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as Element | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      let destination: URL;
      try {
        destination = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      if (destination.origin !== window.location.origin) return;

      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const nextPath = `${destination.pathname}${destination.search}${destination.hash}`;
      if (nextPath === currentPath) return;

      event.preventDefault();
      event.stopPropagation();

      requestConfirmation(() => {
        allowNextNavigationRef.current = true;
        navigate(nextPath);
        setTimeout(() => {
          allowNextNavigationRef.current = false;
        }, 0);
      });
    };

    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [navigate, requestConfirmation, shouldBlock]);

  useEffect(() => {
    recordHrefRef.current = window.location.href;
    let stepTimer: number | undefined;
    let giveUpTimer: number | undefined;
    let releaseTimer: number | undefined;

    const clearLeaveTimers = () => {
      if (stepTimer !== undefined) window.clearTimeout(stepTimer);
      if (giveUpTimer !== undefined) window.clearTimeout(giveUpTimer);
      if (releaseTimer !== undefined) window.clearTimeout(releaseTimer);
      stepTimer = undefined;
      giveUpTimer = undefined;
      releaseTimer = undefined;
    };

    const stopLeaving = () => {
      leavingRef.current = false;
      allowNextNavigationRef.current = false;
      clearLeaveTimers();
    };

    // One history.back() only undoes the duplicate this handler restores, so the
    // URL stays on Record. Refresh leaves those duplicates in the session stack.
    // Keep moving back until the address is a different page.
    const leavePastDuplicateEntries = () => {
      if (leavingRef.current) return;
      leavingRef.current = true;
      allowNextNavigationRef.current = true;

      let steps = 0;
      const maxSteps = window.history.length;

      const step = () => {
        if (!leavingRef.current) return;
        if (window.location.href !== recordHrefRef.current || steps >= maxSteps) {
          stopLeaving();
          return;
        }

        let moved = false;
        steps += 1;

        const onMoved = () => {
          moved = true;
          window.removeEventListener("popstate", onMoved);
          if (!leavingRef.current) return;
          // Duplicate Record entries share this URL, so a successful step can
          // land on the same address. Only a different address means we arrived.
          if (window.location.href !== recordHrefRef.current) {
            stopLeaving();
            return;
          }
          stepTimer = window.setTimeout(step, 0);
        };

        window.addEventListener("popstate", onMoved);
        const traversalId = ++traversalIdRef.current;
        inFlightTraversalRef.current = true;
        window.history.back();
        // Stop walking if this Back never settles. The matching popstate can
        // still arrive later, and it must not be treated as a new Back.
        giveUpTimer = window.setTimeout(() => {
          if (moved) return;
          window.removeEventListener("popstate", onMoved);
          stopLeaving();
          releaseTimer = window.setTimeout(() => {
            if (traversalIdRef.current === traversalId) {
              inFlightTraversalRef.current = false;
            }
          }, LATE_POPSTATE_IGNORE_MS);
        }, HISTORY_TRAVERSAL_GIVE_UP_MS);
      };

      step();
    };

    const onPopState = () => {
      if (inFlightTraversalRef.current) {
        inFlightTraversalRef.current = false;
        if (!leavingRef.current) return;
      }
      if (leavingRef.current || allowNextNavigationRef.current) return;

      if (!shouldBlockRef.current) {
        if (window.location.href === recordHrefRef.current) {
          stepTimer = window.setTimeout(leavePastDuplicateEntries, 0);
        }
        return;
      }

      window.history.pushState(null, "", window.location.href);
      requestConfirmation(leavePastDuplicateEntries);
    };

    if (shouldBlock) {
      window.history.pushState(null, "", window.location.href);
    }

    window.addEventListener("popstate", onPopState);
    return () => {
      inFlightTraversalRef.current = false;
      stopLeaving();
      window.removeEventListener("popstate", onPopState);
    };
  }, [requestConfirmation, shouldBlock]);

  const confirmNavigation = useCallback(() => {
    setShowConfirmation(false);
    const pending = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    pending?.();
  }, []);

  const cancelNavigation = useCallback(() => {
    setShowConfirmation(false);
    pendingNavigationRef.current = null;
  }, []);

  return { showConfirmation, confirmNavigation, cancelNavigation };
}
